/**
 * Clinically-approved intake talk tracks (PRD Appendix A, D2/D12/D13).
 *
 * Static conversation guidance rendered on the intake call guide (Topics step,
 * `IntakeGuideSection`): per-section purpose, suggested questions, follow-up /
 * conditional groups, and listen-for cues. This is the clinical content of
 * record — wording is transcribed verbatim from the signed-off spec and changed
 * as content, never paraphrased or reformatted in the rendering layer (D12).
 *
 * Keyed by `NOTE_SECTIONS` id so the guide, the scratch keys, and the Notes step
 * stay aligned on one taxonomy. Groups flagged `safety` are visually
 * distinguished from routine guidance (D4).
 * @agentic connections
 */

/**
 * A follow-up or conditional group within a talk track (the "↳" branches):
 * a labeled cluster of suggested questions and/or a clinical note. `safety`
 * marks a group whose subject (self-harm / SI, substance withdrawal) must be
 * visually distinguished from routine guidance (D4).
 * @agentic connections
 */
type TalkTrackGroup = {
  label: string;
  questions?: string[];
  /** Clinical guidance shown as a note under the group (not a question). */
  note?: string;
  safety?: boolean;
};

/**
 * A section's talk track: an always-visible purpose line, base suggested
 * questions, follow-up/conditional groups, and listen-for cues.
 * @agentic connections
 */
export type TalkTrack = {
  purpose: string;
  questions?: string[];
  groups?: TalkTrackGroup[];
  /**
   * A section-level framing note, rendered before the listen-for cues (D23).
   * Distinct from a group `note`: it frames the whole section rather than a
   * conditional branch (e.g. Social History's "topics to consider, not a
   * checklist"). Clinical content of record, rendered verbatim (D19).
   */
  note?: string;
  /** Scannable "listen for" cues (the "👂" list). */
  listenFors?: string[];
};

/**
 * Talk-track content of record, keyed by `NOTE_SECTIONS` id. Every authored
 * intake section has an entry (guaranteed by `intake-talk-tracks.test.ts`).
 * @agentic connections
 */
export const INTAKE_TALK_TRACKS: Record<string, TalkTrack> = {
  "chief-complaint": {
    purpose: "Understand why the patient is here, in their own words.",
    questions: [
      "Can you tell me a bit about what's been going on?",
      "Where would you like to start?",
    ],
    groups: [
      {
        label: "Follow-ups",
        questions: [
          "Tell me more about that.",
          "How long has that been going on?",
          "How has it affected your day-to-day life?",
        ],
      },
    ],
  },
  hpi: {
    purpose:
      "Understand current symptoms and how they impact daily functioning.",
    questions: [
      "How long has this been going on?",
      "Has it been getting better, worse, or staying about the same?",
      "How is it affecting your daily life?",
      "What tends to make it better or worse?",
    ],
    listenFors: [
      "Sleep",
      "Relationships",
      "Work",
      "School",
      "Self-care",
      "Functional impairment",
    ],
  },
  "mental-health-history": {
    purpose: "Understand prior treatment, severity, and safety history.",
    questions: [
      "Have you ever worked with a therapist or psychiatrist before?",
      "What was that experience like?",
      "Are you currently seeing anyone?",
      "Have you ever been hospitalized or gone to the emergency room because of mental health concerns?",
      "Have you ever hurt yourself on purpose, or had thoughts of suicide or a plan?",
    ],
    groups: [
      {
        label: "If hospitalized",
        questions: [
          "When was the most recent time?",
          "What was going on then?",
          "Roughly how long did you stay?",
          "Has this happened more than once?",
        ],
      },
      {
        label: "If self-harm / SI / planned attempt",
        safety: true,
        questions: [
          "When did this happen, and what was going on at the time?",
          "Did it ever progress to an attempt?",
          "What helped you stay safe?",
          "Were there multiple episodes?",
        ],
      },
    ],
    listenFors: [
      "Hospitalizations/ED",
      "Self-harm",
      "Suicidal ideation",
      "Harm to others",
      "Unable to care for self",
    ],
  },
  "current-outpatient-treaters": {
    purpose: "Understand current treatment relationships and support systems.",
    questions: [
      "Are you currently working with a therapist?",
      "Are you seeing a psychiatrist?",
      "Have you worked with providers in the past?",
      "What worked well?",
      "What didn't work well?",
    ],
    groups: [
      {
        label: "Medication discussion",
        questions: [
          "Are you taking any medications currently?",
          "Have you taken any medications in the past?",
          "Do they seem helpful?",
          "Have you stopped any medications in the past?",
        ],
      },
      {
        label: "AI usage",
        questions: [
          "Have you used tools like ChatGPT, Gemini, Grok, or other AI tools for therapy or emotional support?",
          "Which tools have you been using?",
        ],
        note: "If yes — suggest purpose-built therapy support tools with privacy, crisis escalation, and licensed-clinician boundaries.",
      },
    ],
  },
  "substance-use-history": {
    purpose:
      "Understand current and historical substance use and associated risks.",
    questions: [
      "I ask everyone this.",
      "Do you currently use alcohol, marijuana, nicotine, or any other substances?",
      "Are you taking anything that is not explicitly prescribed to you or not being taken as prescribed?",
      "How often?",
      "Has it ever caused problems for you?",
    ],
    groups: [
      {
        label: "If any use",
        safety: true,
        questions: [
          "Have you ever gone through detox or treatment?",
          "Have you ever had withdrawal symptoms, or withdrawal seizures?",
        ],
        note: "Pay particular attention to alcohol and benzodiazepine withdrawal.",
      },
    ],
  },
  "medical-history": {
    purpose:
      "Understand medical conditions that may contribute to mental health symptoms.",
    questions: [
      "Do you have any medical conditions you're currently managing?",
      "Do you take any daily medications?",
      "Are there any physical symptoms or health issues that feel connected to what you've been experiencing?",
      "What does your overall health look like today?",
    ],
    listenFors: [
      "Chronic illness",
      "Pain",
      "Sleep disorders",
      "Neurologic conditions",
      "Medical stressors",
    ],
  },
  "mental-status-exam": {
    purpose:
      "Observe throughout the call; document in the clinical note after.",
    groups: [
      {
        label: "Phone reality",
        note: "note anything not assessable by phone (e.g. appearance) as not observed.",
      },
    ],
    listenFors: [
      "Behavior",
      "Speech",
      "Mood",
      "Affect",
      "Thought process",
      "Thought content",
      "Perception",
      "Cognition",
      "Insight & judgment",
    ],
  },
  "social-history": {
    purpose:
      "Understand the patient's environment, supports, and functional impact.",
    questions: [
      "Thinking about the last month, how have symptoms affected work, school, or daily responsibilities?",
      "How many days of work or school have you missed in the last month because of these symptoms or concerns?",
      "Do you have stable housing?",
      "Do you have enough food?",
      "Do you have reliable transportation?",
      "Are finances creating stress or limiting care access?",
      "Who are the people you can rely on?",
    ],
    groups: [
      {
        label: "Family history",
        questions: [
          "Does anyone in your family have a history of mental health conditions?",
          "Has anyone in your family struggled with alcohol or other substance use?",
          "Are there any medical conditions that run in your family?",
        ],
      },
    ],
    note: "Note: These are topics to consider, not a checklist to work through — they often surface on their own elsewhere in the conversation.",
    listenFors: [
      "Days missed",
      "Food",
      "Housing",
      "Transportation",
      "Finances",
      "Social supports",
    ],
  },
  "pediatric-social": {
    purpose:
      "Understand the child's support system, school environment, and developmental context.",
    questions: [
      "Who lives at home?",
      "Any recent changes or transitions?",
      "How is school going?",
      "How are grades and attendance?",
      "What activities do they enjoy?",
      "How are friendships and social relationships?",
    ],
    groups: [
      {
        label: "Family history",
        questions: [
          "Does anyone in the family have a history of mental health conditions or substance use?",
          "Are there any medical conditions that run in the family?",
        ],
      },
    ],
  },
  "pediatric-birth-dev": {
    purpose:
      "Identify developmental or early-life factors that may contribute to current concerns.",
    questions: [
      "Were there any pregnancy or birth complications? (e.g. time spent in NICU)",
      "Were all the developmental milestones met on time? Or were some later or earlier? (e.g. walking, talking, potty training)",
    ],
  },
  "assessment-plan": {
    purpose:
      "Close the call with a formulation, next steps, and patient preferences.",
    questions: [
      "Based on what we discussed, here is what I think the next step could be.",
      "Would it be helpful if we requested therapy, prescribing, or another service?",
      "Would you prefer a follow-up visit to continue care planning?",
      "What preferences or constraints should we account for before we make the plan?",
    ],
    listenFors: [
      "Goals",
      "Referrals",
      "Follow-up timing",
      "Care preferences",
      "Safety needs",
    ],
  },
};

/**
 * Talk track for the Goals card rendered inside the call guide, immediately
 * before Assessment & Plan (amendment §8.4.4/§8.5). Goals are not a
 * clinical-impression note section — they persist as FHIR `Goal` resources — so
 * this track is a **guide-local** export, deliberately kept out of
 * `INTAKE_TALK_TRACKS` (which is 1:1 with `NOTE_SECTIONS`) per D21. The
 * miracle-question group and the SMART note are clinical content of record,
 * rendered verbatim after clinical sign-off (D19).
 * @agentic connections
 */
export const GOALS_TALK_TRACK: TalkTrack = {
  purpose: "Set collaborative treatment goals with the patient.",
  questions: [
    "Suppose tonight, while you slept, a miracle occurred. All of the things that brought you here had suddenly been solved! But because you were asleep, you didn't know that the miracle had happened. When you wake up, what would be the first thing you notice that would tell you life had suddenly gotten better?",
    "What would you notice next?",
    "Who else would notice the changes?",
  ],
  note: "Note: Ensure goals are SMART — specific, measurable, achievable, relevant, and time-bound.",
};
